import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { scrollToId } from "@/lib/scroll";

const TASKS = ["Lead follow-up", "Invoice & data entry", "Support triage", "Booking & reminders"];
const SHARES = [25, 40, 60];

const fmt = (n) =>
    n.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });

export default function CostMeter() {
    const [task, setTask] = useState(TASKS[0]);
    const [hours, setHours] = useState(6);
    const [people, setPeople] = useState(2);
    const [rate, setRate] = useState(35);
    const [share, setShare] = useState(40);

    const { yearly, saved, hoursBack } = useMemo(() => {
        const year = hours * people * rate * 52;
        return {
            yearly: year,
            saved: year * (share / 100),
            hoursBack: hours * people * (share / 100),
        };
    }, [hours, people, rate, share]);

    return (
        <section id="cost-meter" data-testid="cost-meter" className="bg-white px-5 py-14 sm:py-20">
            <div className="mx-auto max-w-6xl">
                <span className="section-kicker">A quick number</span>
                <h2 className="section-title max-w-2xl">What is one repetitive task costing you each year?</h2>
                <p className="section-lead">
                    Pick a task or set your own hours. Nothing is stored, and the result is arithmetic, not a promise.
                </p>

                <div className="mt-8 grid overflow-hidden rounded-3xl ring-1 ring-black/5 lg:grid-cols-2">
                    <div className="bg-[#f6f7fb] p-5 sm:p-8">
                        <div className="flex flex-wrap gap-2">
                            {TASKS.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => setTask(t)}
                                    className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                                        task === t
                                            ? "bg-[#0a0a0b] text-white"
                                            : "bg-white text-[#4b4b55] ring-1 ring-black/10 hover:text-[#0a0a0b]"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>

                        <div className="mt-8 space-y-6">
                            <MeterRow
                                label="Hours a week, per person"
                                display={`${hours} h`}
                                value={hours}
                                min={1}
                                max={20}
                                onChange={setHours}
                            />
                            <MeterRow
                                label="People doing it"
                                display={String(people)}
                                value={people}
                                min={1}
                                max={20}
                                onChange={setPeople}
                            />
                            <MeterRow
                                label="Cost per hour"
                                display={`$${rate}`}
                                value={rate}
                                min={15}
                                max={150}
                                step={5}
                                onChange={setRate}
                            />
                        </div>

                        <p className="mt-8 text-sm font-medium text-[#5b5b66]">Share of it automation could take over</p>
                        <div className="mt-3 flex gap-2">
                            {SHARES.map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setShare(n)}
                                    className={`rounded-full px-4 py-2 text-sm font-semibold ${
                                        share === n
                                            ? "bg-[#0a0a0b] text-white"
                                            : "bg-white text-[#4b4b55] ring-1 ring-black/10"
                                    }`}
                                >
                                    {n}%
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col justify-center bg-white p-5 sm:p-8">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a8a95]">
                            That task costs about
                        </p>
                        <p className="mt-2 font-display text-5xl font-extrabold tracking-tight">
                            <span className="text-gradient">{fmt(yearly)}</span>{" "}
                            <span className="text-lg font-semibold text-[#8a8a95]">/ year</span>
                        </p>
                        <p className="mt-4 text-[15px] leading-relaxed text-[#3a3a44]">
                            Automating {share}% would give back about{" "}
                            <span className="font-semibold">{fmt(saved)}</span> a year, or{" "}
                            <span className="font-semibold">{hoursBack.toLocaleString("en-CA", { maximumFractionDigits: 1 })} hours</span> a
                            week.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={() => scrollToId("#contact")}
                                className="group inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(43,80,255,0.28)]"
                            >
                                Start a project
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <Link
                                to="/services/sales#roi"
                                className="inline-flex items-center rounded-full bg-[#f6f7fb] px-5 py-2.5 text-sm font-semibold text-[#0a0a0b] ring-1 ring-black/10"
                            >
                                See payback
                            </Link>
                        </div>
                        <p className="mt-4 text-xs leading-relaxed text-[#8a8a95]">
                            Arithmetic on your own inputs: hours × people × cost per hour × 52 weeks. An estimate, not a
                            guarantee of savings.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function MeterRow({ label, display, value, min, max, step = 1, onChange }) {
    return (
        <div>
            <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-[#5b5b66]">{label}</span>
                <span className="text-sm font-semibold text-[#0a0a0b]">{display}</span>
            </div>
            <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
        </div>
    );
}
